import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Users, Eye, Sparkles, CreditCard, TrendingUp, RefreshCw } from "lucide-react";
import { NavBar } from "../components/NavBar";
import { useAuth } from "../lib/auth";
import { getAdminAnalytics } from "../lib/api";

const EVENT_LABELS = {
  onboarding_started: "Onboarding started",
  onboarding_completed: "Onboarding completed",
  paywall_viewed: "Paywall viewed",
  yearly_selected: "Yearly selected",
  monthly_selected: "Monthly selected",
  checkout_started: "Checkout started",
  trial_started: "Trial started",
  subscription_active: "Became paid (active)",
  payment_succeeded: "Payment succeeded",
  payment_failed: "Payment failed",
  checkout_abandoned: "Checkout abandoned",
  subscription_expired: "Subscription ended",
};

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="card card-pad" data-testid={testid} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ width: 34, height: 34, borderRadius: 10, background: "var(--brand-tertiary)", display: "grid", placeItems: "center" }}>
        <Icon size={16} color="var(--brand)" />
      </span>
      <span className="eyebrow">{label}</span>
    </div>
    <div className="gauge-num" style={{ fontSize: 34, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ color: "var(--muted)", fontSize: 12.5 }}>{sub}</div>}
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { status, user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      setData(await getAdminAnalytics());
      setErr(null);
    } catch (e) {
      setErr(e.message || "Could not load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (status === "loading") return;
    if (!isAdmin) {
      navigate("/", { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, isAdmin]);

  if (status === "loading" || (isAdmin && loading && !data)) {
    return (
      <>
        <NavBar />
        <div style={{ minHeight: "50vh", display: "grid", placeItems: "center" }}>
          <Loader2 size={32} className="spin" color="var(--brand)" />
        </div>
      </>
    );
  }

  const t = data?.totals || {};
  const f = data?.funnel || {};
  const ts = data?.timeseries || [];
  const maxV = Math.max(1, ...ts.map((d) => Math.max(d.signups, d.conversions, d.paywall_views)));

  return (
    <>
      <NavBar />
      <main className="container-nc" style={{ paddingTop: 36, paddingBottom: 90, maxWidth: 920 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
          <div>
            <span className="eyebrow">Admin</span>
            <h1 style={{ fontSize: "clamp(1.9rem, 5vw, 2.6rem)", fontWeight: 600, letterSpacing: "-0.03em", margin: "8px 0 0" }} data-testid="admin-title">
              Analytics
            </h1>
          </div>
          <button className="btn btn-ghost" onClick={load} disabled={loading} data-testid="admin-refresh">
            {loading ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />} Refresh
          </button>
        </div>

        {err && (
          <p style={{ color: "var(--brand)", marginTop: 16 }} data-testid="admin-error">{err}</p>
        )}

        {/* Top stats */}
        <div className="trait-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginTop: 22 }}>
          <StatCard icon={Users} label="Sign-ups" value={t.users ?? 0} sub={`+${t.signups_7d ?? 0} in 7 days`} testid="stat-signups" />
          <StatCard icon={Eye} label="Paywall views" value={f.paywall_viewed ?? 0} sub={`${t.paywall_to_sub_rate ?? 0}% convert`} testid="stat-paywall" />
          <StatCard icon={Sparkles} label="Trials started" value={f.trial_started ?? 0} sub={`${t.trialing ?? 0} trialing now`} testid="stat-trials" />
          <StatCard icon={CreditCard} label="Subscribers" value={t.paid_or_trialing ?? 0} sub={`${t.active_subscribers ?? 0} paid · ${t.trialing ?? 0} trial`} testid="stat-subs" />
        </div>

        {/* Timeseries */}
        <div className="card card-pad" style={{ marginTop: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
            <TrendingUp size={16} color="var(--brand-2)" />
            <span className="eyebrow">Last 14 days</span>
            <span style={{ marginLeft: "auto", display: "flex", gap: 14, fontSize: 11.5, color: "var(--muted)" }}>
              <Legend color="var(--brand-2)" text="Sign-ups" />
              <Legend color="var(--brand)" text="Conversions" />
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 140 }} data-testid="admin-chart">
            {ts.map((d) => (
              <div key={d.date} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, height: "100%", justifyContent: "flex-end" }} title={`${d.date}: ${d.signups} sign-ups, ${d.conversions} conversions`}>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: "100%", width: "100%", justifyContent: "center" }}>
                  <div style={{ width: "42%", background: "var(--brand-2)", borderRadius: "3px 3px 0 0", height: `${(d.signups / maxV) * 100}%`, minHeight: d.signups ? 3 : 0 }} />
                  <div style={{ width: "42%", background: "var(--brand)", borderRadius: "3px 3px 0 0", height: `${(d.conversions / maxV) * 100}%`, minHeight: d.conversions ? 3 : 0 }} />
                </div>
                <span className="mono" style={{ fontSize: 9, color: "var(--muted)" }}>{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Funnel + recent */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 22 }} className="trait-grid">
          <div className="card card-pad">
            <span className="eyebrow">Funnel &amp; events</span>
            <div style={{ marginTop: 12 }} data-testid="admin-funnel">
              {Object.keys(EVENT_LABELS).map((k) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontSize: 13.5, color: "var(--on-surface-2)" }}>{EVENT_LABELS[k]}</span>
                  <span className="mono" style={{ fontSize: 13.5, fontWeight: 600 }}>{f[k] ?? 0}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 12, color: "var(--muted)", fontSize: 12.5 }}>
              <span>Yearly / Monthly (live)</span>
              <span className="mono">{t.by_plan?.pro_annual ?? 0} / {t.by_plan?.pro_monthly ?? 0}</span>
            </div>
          </div>

          <div className="card card-pad">
            <span className="eyebrow">Recent activity</span>
            <div style={{ marginTop: 12, maxHeight: 420, overflowY: "auto" }} data-testid="admin-recent">
              {(data?.recent || []).length === 0 && <p style={{ color: "var(--muted)", fontSize: 13 }}>No events yet.</p>}
              {(data?.recent || []).map((e, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "7px 0", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontSize: 13, color: "var(--on-surface-2)" }}>{EVENT_LABELS[e.name] || e.name}</span>
                  <span className="mono" style={{ fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>
                    {e.at ? new Date(e.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p style={{ color: "var(--muted)", fontSize: 11.5, marginTop: 16, lineHeight: 1.6 }}>
          Private admin view. Questionnaire answers are never shown here — only aggregate counts and event timestamps.
        </p>
      </main>
    </>
  );
}

const Legend = ({ color, text }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
    <span style={{ width: 9, height: 9, borderRadius: 2, background: color }} /> {text}
  </span>
);
